import { LOG_RING_SIZE } from './constants.js';

export type LogLevel = 'debug' | 'info' | 'warn' | 'error';

export interface LogLine {
  readonly at_ms: number;
  readonly level: LogLevel;
  readonly event: string;
  readonly detail: Readonly<Record<string, unknown>>;
}

export interface Logger {
  debug(event: string, detail?: Record<string, unknown>): void;
  info(event: string, detail?: Record<string, unknown>): void;
  warn(event: string, detail?: Record<string, unknown>): void;
  error(event: string, detail?: Record<string, unknown>): void;
  /** Most recent lines, oldest first. Feeds the heartbeat's last_error and dwf-036. */
  lines(): readonly LogLine[];
  lastError(): LogLine | null;
}

export interface LoggerOptions {
  readonly now: () => number;
  /** Sink for the line; the browser build writes structured JSON to the console. */
  readonly sink?: (line: LogLine) => void;
}

export function createLogger(options: LoggerOptions): Logger {
  const ring: LogLine[] = [];
  const sink = options.sink;

  const write = (level: LogLevel, event: string, detail: Record<string, unknown> = {}): void => {
    const line: LogLine = { at_ms: options.now(), level, event, detail };
    ring.push(line);
    if (ring.length > LOG_RING_SIZE) ring.shift();
    sink?.(line);
  };

  return {
    debug: (event, detail) => write('debug', event, detail),
    info: (event, detail) => write('info', event, detail),
    warn: (event, detail) => write('warn', event, detail),
    error: (event, detail) => write('error', event, detail),
    lines: () => ring.slice(),
    lastError: () => {
      for (let index = ring.length - 1; index >= 0; index -= 1) {
        const line = ring[index];
        if (line !== undefined && line.level === 'error') return line;
      }
      return null;
    },
  };
}

export function consoleSink(line: LogLine): void {
  const payload = JSON.stringify({
    ts: new Date(line.at_ms).toISOString(),
    level: line.level,
    event: line.event,
    ...line.detail,
  });
  if (line.level === 'error') console.error(payload);
  else if (line.level === 'warn') console.warn(payload);
  else console.log(payload);
}
